import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
} from "@mui/material";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";

import AppModal from "../common/AppModal";
import AppLinearProgress from "../common/AppLinearProgress";
import { downloadItemImportTemplate } from "../../redux/actions/itemActions";
import { ItemImportReportDto } from "../../shared/dtos/item/ItemImportReportDto";
import { useAppDispatch } from "../../redux/store";

interface ImportItemsModalProps {
  open: boolean;
  onClose: () => void;
  onImport: (file: File) => void;
  loading: boolean;
  error: string | null;
  report: ItemImportReportDto | null;
}

const ImportItemsModal: React.FC<ImportItemsModalProps> = ({
  open,
  onClose,
  onImport,
  loading,
  error,
  report,
}) => {
  const dispatch = useAppDispatch();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];

    if (file) {
      setSelectedFile(file);
    }

    // Allows selecting the same file again after removing it.
    e.target.value = "";
  };

  const handleImport = (): void => {
    if (selectedFile) {
      onImport(selectedFile);
    }
  };

  const handleRemove = (): void => {
    setSelectedFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownloadTemplate = (): void => {
    dispatch(downloadItemImportTemplate());
  };
  const renderContent = (): React.JSX.Element => {
    if (report) {
      return (
        <Box sx={{ py: 1 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Batch import processed.
          </Alert>
          <Stack direction="row" sx={{ justifyContent: "space-around", mb: 3 }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{ color: "success.main", fontWeight: 700 }}
              >
                {report.importedCount}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Items Imported
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  color:
                    report.skippedCount > 0 ? "warning.main" : "text.secondary",
                  fontWeight: 700,
                }}
              >
                {report.skippedCount}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                Items Skipped
              </Typography>
            </Box>
          </Stack>
          {report.errors?.length > 0 && (
            <Box
              sx={{
                maxHeight: 200,
                overflowY: "auto",
                border: "1px solid",
                borderColor: "divider",
                p: 1,
                borderRadius: 1,
              }}
            >
              <List dense>
                {report.errors.map((err, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemIcon sx={{ minWidth: 30 }}>
                      <ErrorOutlineIcon color="warning" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={err}
                      slotProps={{
                        primary: {
                          variant: "caption",
                          sx: { color: "error.main" },
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            color="success"
            sx={{ mt: 3 }}
          >
            Done
          </Button>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <AppLinearProgress label="Uploading & Validating... Please wait." />
        </Box>
      );
    }

    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Upload an Excel file (.xlsx, .xls) matching the system template.
          </Typography>
          <Link
            component="button"
            variant="body2"
            color="secondary"
            onClick={handleDownloadTemplate}
            sx={{ mt: 1, fontWeight: "bold" }}
          >
            Download Excel Template
          </Link>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box
          sx={{
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            bgcolor: selectedFile ? "action.hover" : "transparent",
          }}
        >
          {selectedFile ? (
            <Stack spacing={1} sx={{ alignItems: "center" }}>
              <DescriptionIcon color="success" sx={{ fontSize: 40 }} />
              <Typography sx={{ fontWeight: 600 }}>
                {selectedFile.name}
              </Typography>
              <Button size="small" color="error" onClick={handleRemove}>
                Remove
              </Button>
            </Stack>
          ) : (
            <>
              <UploadFileIcon
                sx={{ fontSize: 50, color: "text.secondary", mb: 1 }}
              />
              <Typography sx={{ mb: 2 }}>No file selected</Typography>
              <Button variant="outlined" color="secondary" component="label">
                Browse Files
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
              </Button>
            </>
          )}
        </Box>

        <Button
          variant="outlined"
          fullWidth
          disabled={!selectedFile || loading}
          onClick={handleImport}
          color="secondary"
        >
          Start Import
        </Button>
      </Stack>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={loading ? () => {} : onClose}
      title="Import Items"
      width="sm"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ImportItemsModal;
